import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useInactiveTeamMembers } from "@/hooks/useTeamManagement";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontal } from "lucide-react";
import { ReactivateUserDialog } from "./ReactivateUserDialog";
import type { Database } from "@/integrations/supabase/types";

type UserRole = {
  role_id: string;
  role: Database["public"]["Tables"]["role"]["Row"] | null;
};

export function InactiveUsersTable() {
  const { data: members, isLoading } = useInactiveTeamMembers();

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading inactive users...</div>;
  }

  if (!members || members.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No inactive users found.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Roles</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Deactivated</TableHead>
          <TableHead className="w-[80px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => (
          <TableRow key={member.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={member.avatar_url || ""} />
                  <AvatarFallback>
                    {member.display_name?.charAt(0) || member.email.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium">{member.display_name || "—"}</span>
              </div>
            </TableCell>
            <TableCell>{member.email}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {member.user_role?.map((ur: UserRole) => (
                  <Badge key={ur.role_id} variant="secondary" className="capitalize">
                    {ur.role?.name?.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="secondary">Inactive</Badge>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDistanceToNow(new Date(member.updated_at), { addSuffix: true })}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <ReactivateUserDialog user={member} />
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
